import { useEffect, useState } from 'react';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../types';

export type ClipboardHelperProps = {
	issue: ListIssueForCopyPaste;
	field: ListIssueFieldTransformed;
	clipboardItem?: ClipboardItem;
	onSuccess?: (result: ClipboardItem) => void;
	onComplete: (success: boolean, error?: Error) => void;
	metadata: PasteMetadata | null;
	action: 'copy' | 'paste' | 'cut';
	onBeforeAction?: () => void;
};

export const createClipboardHelperComponent =
	(useFieldPasteHandler: PasteHandler) =>
	({
		issue,
		field,
		clipboardItem,
		onComplete,
		onSuccess,
		metadata,
		action,
		onBeforeAction,
	}: ClipboardHelperProps) => {
		const [hasBeenCalled, setHasBeenCalled] = useState(false);
		const { copyHandler, pasteHandler, cutHandler } = useFieldPasteHandler(issue, field);

		useEffect(() => {
			function handleCopy() {
				if (hasBeenCalled) {
					return;
				}
				setHasBeenCalled(true);
				if (!copyHandler) {
					onComplete(false, new Error('No copy handler provided'));
					return;
				}

				try {
					const result = copyHandler();
					if (result) {
						onSuccess?.(result);
						onComplete(true);
					} else {
						onComplete(false, new Error('Failed to copy'));
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					onComplete(false, error);
				}
			}
			if (action === 'copy') {
				handleCopy();
			}
		}, [onComplete, hasBeenCalled, action, copyHandler, onSuccess]);

		useEffect(() => {
			async function handlePaste() {
				if (!clipboardItem || hasBeenCalled) {
					return;
				}
				setHasBeenCalled(true);
				onBeforeAction?.();
				try {
					const success = await pasteHandler(clipboardItem, metadata);
					if (success) {
						onComplete(true);
					} else {
						onComplete(false, new Error('Generic server error'));
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					onComplete(false, error);
				}
			}

			if (action === 'paste') {
				handlePaste();
			}
		}, [clipboardItem, onComplete, pasteHandler, metadata, hasBeenCalled, action, onBeforeAction]);

		useEffect(() => {
			async function handleCut() {
				if (hasBeenCalled) {
					return;
				}
				setHasBeenCalled(true);
				onBeforeAction?.();
				try {
					const success = await cutHandler();
					if (success) {
						onComplete(true);
					} else {
						onComplete(false, new Error('Generic server error'));
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					onComplete(false, error);
				}
			}

			if (action === 'cut') {
				handleCut();
			}
		}, [clipboardItem, onComplete, cutHandler, metadata, hasBeenCalled, action, onBeforeAction]);

		return null;
	};
